import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    signInWithPopup, 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    signOut as firebaseSignOut, 
    onAuthStateChanged 
} from 'firebase/auth';
import { ref, onValue, update, get, query, orderByChild, equalTo, remove, push } from 'firebase/database';
import { auth, googleProvider, db, app } from '../../firebase';
import { SKINS, CASES, RARITIES, DAILY_REWARDS, BUNDLES, COIN_PACKS } from '../Shop/ShopConfig'; 
// Cloud Functions Imports
import { getFunctions, httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

// ... (Date Helpers) ...
const getWeekId = () => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(),0,1);
    const weekNo = Math.ceil(( ( (d - yearStart) / 86400000) + 1)/7);
    return `${d.getFullYear()}_${weekNo}`;
};

const getMonthId = () => {
    const d = new Date();
    return `${d.getFullYear()}_${d.getMonth() + 1}`;
};

const isSameWeek = (timestamp) => {
    if (!timestamp) return false;
    const d = new Date(timestamp);
    const now = new Date();
    const getW = (date) => {
        const d = new Date(date.valueOf());
        d.setHours(0,0,0,0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const ys = new Date(d.getFullYear(),0,1);
        return Math.ceil((((d - ys) / 86400000) + 1)/7);
    };
    return d.getFullYear() === now.getFullYear() && getW(d) === getW(now);
};

const isSameMonth = (timestamp) => {
    if (!timestamp) return false;
    const d = new Date(timestamp);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
};

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notificationCount, setNotificationCount] = useState(0);

    // --- Search & Friend Status ---
    const searchUserByUsername = async (username) => {
        const usersRef = ref(db, 'users');
        const q = query(usersRef, orderByChild('username'), equalTo(username));
        try {
            const snapshot = await get(q);
            if (snapshot.exists()) return Object.keys(snapshot.val())[0];
            return null;
        } catch (err) {
            console.error("Search error:", err);
            return null;
        }
    };

    const checkFriendStatus = async (targetUid) => {
        if (!currentUser || !userData) return 'none';
        if (userData.friends && userData.friends[targetUid]) return 'friends';
        try {
            const reqRef = ref(db, `users/${targetUid}/friendRequests/${currentUser.uid}`);
            const snapshot = await get(reqRef);
            if (snapshot.exists()) return 'pending';
        } catch (e) { console.error(e); }
        return 'none';
    };

    const sendFriendRequest = async (targetUid) => {
        if (!currentUser || targetUid === currentUser.uid) return false;
        if (userData?.friends?.[targetUid]) return 'already_friends';
        try {
            const updates = {};
            updates[`users/${targetUid}/friendRequests/${currentUser.uid}`] = true;
            await update(ref(db), updates);
            return 'success';
        } catch (e) { return 'error'; }
    };

    const acceptFriendRequest = async (requesterUid) => {
        if (!currentUser) return;
        try {
            const updates = {};
            updates[`users/${currentUser.uid}/friends/${requesterUid}`] = true;
            updates[`users/${requesterUid}/friends/${currentUser.uid}`] = true;
            updates[`users/${currentUser.uid}/friendRequests/${requesterUid}`] = null;
            await update(ref(db), updates);
        } catch (e) { console.error(e); }
    };

    const acceptInviteLink = async (inviterUid) => {
        if (!currentUser) return;
        try {
            const updates = {};
            updates[`users/${currentUser.uid}/friends/${inviterUid}`] = true;
            updates[`users/${inviterUid}/friends/${currentUser.uid}`] = true;
            updates[`users/${currentUser.uid}/friendRequests/${inviterUid}`] = null;
            updates[`users/${inviterUid}/friendRequests/${currentUser.uid}`] = null;
            await update(ref(db), updates);
            return 'success';
        } catch (e) { return 'error'; }
    };

    const removeFriendRequest = async (targetUid, isIncoming = true) => {
        if (!currentUser) return;
        const path = isIncoming 
            ? `users/${currentUser.uid}/friendRequests/${targetUid}`
            : `users/${targetUid}/friendRequests/${currentUser.uid}`;
        try { await remove(ref(db, path)); } catch (e) { console.error(e); }
    };

    const removeFriend = async (friendUid) => {
        if (!currentUser) return;
        try {
            const updates = {};
            updates[`users/${currentUser.uid}/friends/${friendUid}`] = null;
            updates[`users/${friendUid}/friends/${currentUser.uid}`] = null;
            await update(ref(db), updates);
        } catch (e) { console.error(e); }
    };

    const getPublicProfile = async (uid) => {
        try {
            const snap = await get(ref(db, `users/${uid}`));
            if (snap.exists()) {
                const data = snap.val();
                return {
                    uid,
                    username: data.username,
                    avatarId: data.avatarId || 0,
                    highscore: data.highscore?.alltime?.score || 0
                };
            }
        } catch (e) { console.error(e); }
        return null;
    };

    // --- CHALLENGE SYSTEM ---
    const createChallenge = async (targetUid, wager, score, coinsCollected = 0) => {
        if (!currentUser || !userData) return;
        try {
            const updates = {};
            const challengeId = push(ref(db, `users/${targetUid}/challenges`)).key;
            
            const currentMoney = userData.money || 0;
            updates[`users/${currentUser.uid}/money`] = currentMoney - wager + coinsCollected;

            updates[`users/${targetUid}/challenges/${challengeId}`] = {
                senderUid: currentUser.uid,
                senderName: userData.username || 'Player',
                senderAvatarId: userData.avatarId || 0,
                wager: wager,
                scoreToBeat: score,
                timestamp: Date.now()
            };

            await update(ref(db), updates);
            return 'success';
        } catch (e) { return 'error'; }
    };

    const removeChallenge = async (challengeId) => {
        if (!currentUser) return;
        try {
           const snap = await get(ref(db, `users/${currentUser.uid}/challenges/${challengeId}`));
           if(snap.exists()) {
               const updates = {};
               updates[`users/${currentUser.uid}/challenges/${challengeId}`] = null;
               await update(ref(db), updates);
           }
        } catch (e) { console.error(e); }
    };

    const resolveChallenge = async (challengeId, challengeData, myScore, coinsCollected = 0) => {
        if (!currentUser || !userData) return;
        if (!challengeId) return 'error';

        const { senderUid, wager, scoreToBeat } = challengeData;
        const updates = {};
        const currentMoney = userData.money || 0;

        updates[`users/${currentUser.uid}/challenges/${challengeId}`] = null;

        let outcome = 'loss'; 
        if (myScore > scoreToBeat) {
            outcome = 'win';
        }

        if (outcome === 'win') {
            updates[`users/${currentUser.uid}/money`] = currentMoney + wager + coinsCollected;
        } else {
            updates[`users/${currentUser.uid}/money`] = currentMoney - wager + coinsCollected;
        }

        if (senderUid) {
            try {
                const notifRef = push(ref(db, `users/${senderUid}/challengeNotifications`));
                const notifId = notifRef.key;
                
                const baseNotif = {
                    opponentName: userData.username || 'Player',
                    wager: wager,
                    score: myScore,
                    timestamp: Date.now()
                };

                if (outcome === 'win') {
                    updates[`users/${senderUid}/challengeNotifications/${notifId}`] = { ...baseNotif, type: 'loss' };
                } else {
                    updates[`users/${senderUid}/challengeNotifications/${notifId}`] = { ...baseNotif, type: 'win', rewardAmount: wager * 2 };
                }
            } catch (err) { }
        }

        try {
            await update(ref(db), updates);
            return outcome;
        } catch (e) { return 'error'; }
    };

    const dismissNotification = async (id, notification) => {
        if (!currentUser) return;
        const updates = {};
        updates[`users/${currentUser.uid}/challengeNotifications/${id}`] = null;
        if (notification.type === 'win' && notification.rewardAmount) {
             const currentMoney = userData.money || 0;
             updates[`users/${currentUser.uid}/money`] = currentMoney + notification.rewardAmount;
        }
        try { await update(ref(db), updates); } catch (e) { console.error(e); }
    };

    // --- SHOP SYSTEM ---

    // 1. Buy & Open Case Logic
    const purchaseCase = async (caseId) => {
        if (!currentUser || !userData) return { success: false, error: 'Not logged in' };
        
        const caseItem = CASES.find(c => c.id === caseId);
        if (!caseItem) return { success: false, error: 'Case not found' };

        if ((userData.money || 0) < caseItem.price) {
            return { success: false, error: 'Not enough money' };
        }

        const potentialSkins = SKINS.filter(s => caseItem.contents.includes(s.id));
        let totalWeight = 0;
        const weightedSkins = potentialSkins.map(skin => {
            const rarity = RARITIES[skin.rarity];
            const weight = rarity.probability * 100; 
            totalWeight += weight;
            return { ...skin, weight };
        });

        let random = Math.random() * totalWeight;
        let selectedSkin = weightedSkins[0];

        for (let skin of weightedSkins) {
            if (random < skin.weight) {
                selectedSkin = skin;
                break;
            }
            random -= skin.weight;
        }

        const updates = {};
        let finalMoney = (userData.money || 0) - caseItem.price;
        
        let isDuplicate = false;
        let refundAmount = 0;

        if (userData.skins?.owned?.[selectedSkin.id]) {
            isDuplicate = true;
            refundAmount = Math.floor(caseItem.price * 0.5); 
            finalMoney += refundAmount;
        } else {
            updates[`users/${currentUser.uid}/skins/owned/${selectedSkin.id}`] = true;
        }

        updates[`users/${currentUser.uid}/money`] = finalMoney;

        try {
            await update(ref(db), updates);
            return { 
                success: true, 
                reward: selectedSkin,
                isDuplicate: isDuplicate,
                refundAmount: refundAmount 
            };
        } catch (e) {
            console.error("Shop purchase error:", e);
            return { success: false, error: 'Transaction failed' };
        }
    };

    // 2. Equip Skin
    const equipSkin = async (skinId) => {
        if (!currentUser) return;
        const skin = SKINS.find(s => s.id === skinId);
        const type = skin?.type || 'player'; 
        const isOwned = (userData?.skins?.owned?.[skinId]) || skinId === 'default' || skinId === 'tile_default';

        if (isOwned) {
            const updates = {};
            if (type === 'player') {
                updates[`users/${currentUser.uid}/skins/equippedPlayer`] = skinId;
            } else if (type === 'tile') {
                updates[`users/${currentUser.uid}/skins/equippedTile`] = skinId;
            }
            await update(ref(db), updates);
        }
    };

    // 3. Claim Daily Gift
    const claimDailyGift = async () => {
        if (!currentUser || !userData) return { success: false, error: 'Not logged in' };
        
        const now = new Date();
        const lastClaimed = userData.dailyGift?.lastClaimed || 0;
        const currentStreak = userData.dailyGift?.streak || 0;
        
        const lastDate = new Date(lastClaimed);
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        if (lastDate.toDateString() === now.toDateString()) {
             return { success: false, error: 'Already claimed today' };
        }

        let newStreak = 1;
        if (lastDate.toDateString() === yesterday.toDateString()) {
            newStreak = currentStreak + 1;
        }
        if (newStreak > 7) newStreak = 1; 

        const reward = DAILY_REWARDS[newStreak - 1] || DAILY_REWARDS[0];

        const updates = {};
        updates[`users/${currentUser.uid}/money`] = (userData.money || 0) + reward;
        updates[`users/${currentUser.uid}/dailyGift/lastClaimed`] = now.getTime();
        updates[`users/${currentUser.uid}/dailyGift/streak`] = newStreak;
        
        try {
            await update(ref(db), updates);
            return { success: true, reward, streak: newStreak };
        } catch (e) {
            console.error(e);
            return { success: false, error: 'Transaction failed' };
        }
    };
    
    // 4. Purchase Bundle (Redirects to Stripe or handles Coins)
    const purchaseBundle = async (bundleId) => {
        if (!currentUser || !userData) return { success: false, error: 'Not logged in' };
        
        const bundle = BUNDLES.find(b => b.id === bundleId);
        if (!bundle) return { success: false, error: 'Bundle not found' };

        // REAL MONEY (STRIPE)
        if (typeof bundle.price !== 'number') {
            try {
                // Ensure correct region
                const functions = getFunctions(app, 'europe-west1');
                const createSession = httpsCallable(functions, 'createCheckoutSession');
                
                const response = await createSession({ itemId: bundleId });
                
                if (response.data.url) {
                    window.location.href = response.data.url;
                    return { success: true, redirecting: true };
                }
            } catch (error) {
                console.error("Payment init failed:", error);
                return { success: false, error: 'Payment initialization failed' };
            }
            return { success: false, error: 'Unknown error' };
        }

        // VIRTUAL CURRENCY
        let cost = bundle.price;
        if ((userData.money || 0) < cost) {
            return { success: false, error: 'Not enough money' };
        }

        const updates = {};
        let newBalance = (userData.money || 0) - cost;
        if (bundle.contents && bundle.contents.coins) {
             newBalance += bundle.contents.coins;
        }
        updates[`users/${currentUser.uid}/money`] = newBalance;

        if (bundle.contents && bundle.contents.skins) {
            bundle.contents.skins.forEach(skinId => {
                updates[`users/${currentUser.uid}/skins/owned/${skinId}`] = true;
            });
        }
        if (bundle.contents && bundle.contents.noAds) {
            updates[`users/${currentUser.uid}/noAds`] = true;
        }
        updates[`users/${currentUser.uid}/bundles/${bundleId}`] = true;

        try {
            await update(ref(db), updates);
            return { success: true };
        } catch (e) {
            console.error(e);
            return { success: false, error: 'Transaction failed' };
        }
    };

    // 5. Purchase Coin Pack (Redirects to Stripe)
    const purchaseCoinPack = async (packId) => {
        if (!currentUser) return { success: false, error: 'Not logged in' };

        const pack = COIN_PACKS.find(p => p.id === packId);
        if (!pack) return { success: false, error: 'Pack not found' };

        try {
            // Ensure correct region matches your backend
            const functions = getFunctions(app, 'europe-west1');
            const createSession = httpsCallable(functions, 'createCheckoutSession');
            
            // We reuse the same itemId parameter the backend expects
            const response = await createSession({ itemId: packId });
            
            if (response.data.url) {
                window.location.href = response.data.url;
                return { success: true, redirecting: true };
            }
        } catch (error) {
            console.error("Coin payment init failed:", error);
            return { success: false, error: 'Payment initialization failed' };
        }
        return { success: false, error: 'Unknown error' };
    };

    // 6. Verify Payment (Called from App.js after redirect)
    const verifyPayment = async (sessionId) => {
        try {
            const functions = getFunctions(app, 'europe-west1');
            const verifyFn = httpsCallable(functions, 'verifyPayment');
            const result = await verifyFn({ sessionId });
            return result.data; // { success: true, bundleId: '...' }
        } catch (error) {
            console.error("Verification failed:", error);
            return { success: false, error: error.message };
        }
    };

    const initializeStarterOffer = async () => {
        if (!currentUser || !userData) return;
        if (userData.createdAt) return;
        if (userData.offerStartedAt) return;

        const updates = {};
        updates[`users/${currentUser.uid}/offerStartedAt`] = Date.now();
        try { await update(ref(db), updates); } catch (e) {}
    };

    // --- Auth & Profile ---
    const loginWithGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const userRef = ref(db, `users/${result.user.uid}`);
            const snapshot = await get(userRef);
            if (!snapshot.exists()) {
                await update(userRef, {
                    username: result.user.displayName || 'Player',
                    email: result.user.email,
                    avatarId: 0,
                    money: 0,
                    createdAt: Date.now(), 
                    highscore: { alltime: { score: 0, timestamp: Date.now() } }
                });
            }
            return result;
        } catch (error) { throw error; }
    };

    const loginWithEmail = (email, password) => { return signInWithEmailAndPassword(auth, email, password); };

    const registerWithEmail = async (email, password, username) => {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await update(ref(db, `users/${result.user.uid}`), {
            username: username || 'Player',
            email: email,
            avatarId: 0,
            money: 0,
            createdAt: Date.now(),
            highscore: { alltime: { score: 0, timestamp: Date.now() } }
        });
        return result;
    };

    const logout = () => { return firebaseSignOut(auth); };

    const updateUserProfile = async (updates) => {
        if (!currentUser) return;
        const updatesMap = {};
        Object.keys(updates).forEach(key => { updatesMap[`users/${currentUser.uid}/${key}`] = updates[key]; });
        if (userData && (updates.username || updates.avatarId !== undefined)) {
            const newUsername = updates.username || userData.username;
            const newAvatarId = updates.avatarId !== undefined ? updates.avatarId : userData.avatarId;
            if (userData.highscore?.alltime?.score > 0) {
                updatesMap[`leaderboards/alltime/${currentUser.uid}/username`] = newUsername;
                if (updates.avatarId !== undefined) updatesMap[`leaderboards/alltime/${currentUser.uid}/avatarId`] = newAvatarId;
            }
             if (userData.highscore?.weekly?.score > 0 && isSameWeek(userData.highscore.weekly.timestamp)) {
                const weekId = getWeekId();
                updatesMap[`leaderboards/weekly_${weekId}/${currentUser.uid}/username`] = newUsername;
                if (updates.avatarId !== undefined) updatesMap[`leaderboards/weekly_${weekId}/${currentUser.uid}/avatarId`] = newAvatarId;
            }
            if (userData.highscore?.monthly?.score > 0 && isSameMonth(userData.highscore.monthly.timestamp)) {
                const monthId = getMonthId();
                updatesMap[`leaderboards/monthly_${monthId}/${currentUser.uid}/username`] = newUsername;
                if (updates.avatarId !== undefined) updatesMap[`leaderboards/monthly_${monthId}/${currentUser.uid}/avatarId`] = newAvatarId;
            }
        }
        return update(ref(db), updatesMap);
    };

    // FIXED: Now explicitly returns true on success, false on failure
    const saveScore = async (newScore, coinsCollected = 0) => {
        if (!currentUser || !userData) return false;
        const updates = {};
        const now = Date.now();
        const username = userData.username || 'Player';
        const avatarId = userData.avatarId || 0;
        
        if (coinsCollected > 0) {
            updates[`users/${currentUser.uid}/money`] = (userData.money || 0) + coinsCollected;
        }

        const currentAllTime = userData.highscore?.alltime?.score || 0;
        if (newScore > currentAllTime) {
            updates[`users/${currentUser.uid}/highscore/alltime`] = { score: newScore, timestamp: now };
            updates[`leaderboards/alltime/${currentUser.uid}`] = { score: newScore, username, avatarId, timestamp: now };
        }
        
        const weekId = getWeekId();
        const currentWeeklyData = userData.highscore?.weekly;
        const currentWeeklyScore = (currentWeeklyData && isSameWeek(currentWeeklyData.timestamp)) ? currentWeeklyData.score : 0;
        if (newScore > currentWeeklyScore) {
             updates[`users/${currentUser.uid}/highscore/weekly`] = { score: newScore, timestamp: now };
             updates[`leaderboards/weekly_${weekId}/${currentUser.uid}`] = { score: newScore, username, avatarId, timestamp: now };
        }
        
        const monthId = getMonthId();
        const currentMonthlyData = userData.highscore?.monthly;
        const currentMonthlyScore = (currentMonthlyData && isSameMonth(currentMonthlyData.timestamp)) ? currentMonthlyData.score : 0;
        if (newScore > currentMonthlyScore) {
             updates[`users/${currentUser.uid}/highscore/monthly`] = { score: newScore, timestamp: now };
             updates[`leaderboards/monthly_${monthId}/${currentUser.uid}`] = { score: newScore, username, avatarId, timestamp: now };
        }
        
        if (Object.keys(updates).length > 0) {
            try { 
                await update(ref(db), updates); 
                return true; 
            } catch (err) { 
                console.error("Failed to save score/coins:", err);
                return false;
            }
        }
        return false;
    };

    // --- Main Listener ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            if (user) {
                const userRef = ref(db, `users/${user.uid}`);
                const unsubDb = onValue(userRef, (snapshot) => {
                    const data = snapshot.val();
                    setUserData(data);
                    
                    let count = 0;
                    if (data?.friendRequests) count += Object.keys(data.friendRequests).length;
                    if (data?.challenges) count += Object.keys(data.challenges).length;
                    if (data?.challengeNotifications) count += Object.keys(data.challengeNotifications).length;
                    
                    setNotificationCount(count);
                    
                    setLoading(false);
                });
                return () => unsubDb();
            } else {
                setUserData(null);
                setNotificationCount(0);
                setLoading(false);
            }
        });
        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userData,
        loading,
        notificationCount, 
        loginWithGoogle,
        loginWithEmail,
        registerWithEmail,
        logout,
        updateUserProfile,
        saveScore,
        searchUserByUsername,
        checkFriendStatus,
        sendFriendRequest,
        acceptFriendRequest,
        acceptInviteLink,
        removeFriendRequest,
        removeFriend,
        getPublicProfile,
        createChallenge,   
        removeChallenge,   
        resolveChallenge,
        dismissNotification,
        purchaseCase, 
        equipSkin,
        claimDailyGift,
        purchaseBundle,
        purchaseCoinPack, 
        verifyPayment, 
        initializeStarterOffer 
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}